import { useState, type ReactNode } from 'react'
import { Button, ReceiptsFilters, SelectField, StatusMessage, ToggleChips, type DayRange } from '@krp/ui-kit'
import { analyticsView } from '../../cashier-analytics/model/analytics'
import type { AnalyticsPage, AnalyticsReceipt, AnalyticsSummary } from '../../cashier-analytics/model/types'
import { SalesBreakdown } from '../../cashier-analytics/ui/SalesBreakdown'
import { SourceProblems } from '../../cashier-analytics/ui/SourceProblems'
import '../../cashier-analytics/ui/cashiers.css'
import { pointSourceNames, problemText, registersText, type SalesPointProblem } from '../model/format'
import { activeRegisters, summaryOfRegisters, type RegisterChoice } from '../model/registerFilter'
import type { SalesPoint, SalesPointReceiptsKind } from '../model/types'
import { ShopDayReceipts } from './ShopDayReceipts'
import './sales-points.css'

export interface SalesPointStatisticsProps {
  point: SalesPoint | undefined
  /** Номер открытого магазина: по нему выбран пункт списка, пока сам магазин ещё читается. */
  pointId: string
  /** Магазины для переключения прямо в статистике (без архивных). */
  points: readonly Pick<SalesPoint, 'id' | 'name'>[]
  selectPoint: (id: string) => void
  /** Кассы магазина кнопками отбора; пока их меньше двух, отбирать нечего и кнопок нет. */
  registers: readonly RegisterChoice[]
  /** Выбранные кассы; пусто — активны все. */
  chosenRegisters: readonly string[]
  toggleRegister: (id: string) => void
  /** Кассиры для отбора статистики магазина; пока список пуст, выпадающего «Кассир» нет. */
  cashiers?: readonly { id: string; name: string }[]
  /** Выбранный кассир; пусто — все кассиры магазина. */
  cashier?: string
  selectCashier?: (personId: string) => void
  range: DayRange
  today: string
  timeZoneId: string
  summary: AnalyticsSummary | undefined
  busy: boolean
  /** Месяц для графика ещё читается, хотя итоги выбранного дня уже на экране. */
  trendBusy?: boolean
  /** Открытая вкладка «Чеков за день» (RP-ST17): «Чеки» или «Чеки коррекции»; `receipts` — её страница. */
  receiptsKind: SalesPointReceiptsKind
  selectReceiptsKind: (kind: SalesPointReceiptsKind) => void
  /** Чеки выбранного дня открытой вкладки; читаются только когда выбран один день. */
  receipts?: AnalyticsPage | undefined
  receiptsBusy?: boolean
  receiptsError?: SalesPointProblem | null
  moreReceipts?: () => void
  openReceipt: (originalPath: string) => void
  openReceiptError?: string | null
  /** Товары чека под раскрытой строкой (их читает binding источника). */
  receiptLines?: (item: AnalyticsReceipt) => ReactNode
  error: SalesPointProblem | null
  /** Править группу можно, если есть право управлять и доступ ко всем её источникам. */
  canConfigure: boolean
  patch: (value: Partial<{ from: string; to: string; timeZoneId: string }>) => void
  reload: () => void
  configure: () => void
}
const ZONES = [{ value: 'UTC', label: 'UTC' }, { value: 'Europe/Moscow', label: 'Москва, Санкт-Петербург · UTC+3' }, { value: 'Asia/Yekaterinburg', label: 'Екатеринбург · UTC+5' }]

/**
 * Статистика магазина: вверху выбор магазина, «Обновить» и «Настройки» (часовой пояс, состав, правка); дальше общие части выбора
 * дней и итогов (`ReceiptsFilters`); список чеков — только за один день, общей таблицей, вкладками «Чеки» и «Чеки коррекции»; нажатие раскрывает товары чека, «Подробнее» открывает окно источника поверх статистики. Прогноз месяца
 * и график уже внутри общих частей.
 */
export function SalesPointStatisticsScreen(props: SalesPointStatisticsProps) {
  const summary = summaryOfRegisters(props.summary, props.chosenRegisters)
  const view = analyticsView(summary, props.range.from, props.range.to, '')
  const pointOptions = props.points.some((item) => item.id === props.pointId) || props.point === undefined
    ? props.points.map((item) => ({ value: item.id, label: item.name }))
    : [{ value: props.pointId, label: props.point.name }, ...props.points.map((item) => ({ value: item.id, label: item.name }))]
  // Итоги по источникам и кассам — по просьбе человека: сверху общая картина магазина.
  const [details, setDetails] = useState(false)
  // Часовой пояс, состав магазина и его настройка — за одной кнопкой «Настройки»: на телефоне они не занимают экран.
  const [settings, setSettings] = useState(false)
  const singleDay = props.range.from === props.range.to
  return <section className="sales-points" aria-label="Статистика магазина">
    <div className="sales-points__bar">
      <SelectField label="Магазин" actionId="receipts.sales-points.point" value={props.pointId} options={pointOptions} onChange={props.selectPoint} />
      <Button actionId="receipts.sales-points.stats-reload" busy={props.busy} onClick={props.reload}>Обновить</Button>
      <Button actionId="receipts.sales-points.settings" aria-expanded={settings} aria-controls="sales-points-settings" onClick={() => setSettings(!settings)}>Настройки</Button>
    </div>
    {settings && <div id="sales-points-settings" className="sales-points__settings" role="group" aria-label="Настройки магазина">
      <SelectField label="Часовой пояс отчёта" actionId="receipts.sales-points.zone" value={props.timeZoneId} options={ZONES} onChange={(timeZoneId) => props.patch({ timeZoneId })} />
      {props.point !== undefined && <small>{registersText(props.point.registers.length)} · {pointSourceNames(props.point).join(', ')}</small>}
      {props.canConfigure && <Button actionId="receipts.sales-points.configure" onClick={props.configure}>Изменить магазин</Button>}
    </div>}
    {(props.cashiers?.length ?? 0) > 0 && <SelectField label="Кассир" actionId="receipts.sales-points.cashier" value={props.cashier ?? ''}
      options={[{ value: '', label: 'Все кассиры' }, ...(props.cashiers ?? []).map((item) => ({ value: item.id, label: item.name }))]} onChange={(personId) => props.selectCashier?.(personId)} />}
    {props.registers.length > 1 && <ToggleChips label="Кассы магазина" items={props.registers} active={activeRegisters(props.registers, props.chosenRegisters)} onToggle={props.toggleRegister} />}
    {props.error !== null && <StatusMessage kind="error" title="Не удалось получить данные" text={problemText(props.error)} />}
    <div className="sales-points__body">
      <ReceiptsFilters today={props.today} period={props.range} maxDays={31} onPeriod={(range) => props.patch(range)}
        cashiers={[]} cashier="" onCashier={() => undefined} cashierControl={null}
        stats={view.stats} statsBusy={props.busy} trend={view.trend} trendBusy={props.trendBusy ?? props.busy}
        {...(props.summary === undefined && !props.busy ? { trendMessage: 'Данные магазина пока не получены' } : {})}
        kinds={[]} kind="" onKind={() => undefined} search="" searchLabel="Номер чека" onSearch={() => undefined}
        searchShown={false}
        days={[]} onDay={(day) => props.patch({ from: day, to: day })}
        notices={<SourceProblems summary={props.summary} timeZoneId={props.timeZoneId} />} />
      {singleDay && <ShopDayReceipts timeZoneId={props.timeZoneId} kind={props.receiptsKind} selectKind={props.selectReceiptsKind} page={props.receipts} busy={props.receiptsBusy ?? false}
        error={props.receiptsError ?? null} more={props.moreReceipts ?? (() => undefined)} open={props.openReceipt} openError={props.openReceiptError ?? null}
        {...(props.receiptLines === undefined ? {} : { receiptLines: props.receiptLines })} />}
      <div className="sales-points__toolbar">
        <Button actionId="receipts.sales-points.details" aria-expanded={details} onClick={() => setDetails(!details)}>
          {details ? 'Скрыть подробности' : 'Разобрать подробно'}
        </Button>
      </div>
      {details && <SalesBreakdown summary={summary} from={props.range.from} to={props.range.to} place="" />}
    </div>
  </section>
}
